import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  markNotificationRead,
  markAllNotificationsRead,
} from '../utils/notifications';

function formatTime(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('en-ZA', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Shared notification dropdown for admin and student portals.
 */
export default function NotificationBell({ subscribe, variant = 'light' }) {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => subscribe(setNotifications), [subscribe]);

  const unread = notifications.filter((n) => !n.read).length;
  const isDark = variant === 'dark';

  async function handleClick(n) {
    if (!n.read) await markNotificationRead(n.id).catch(console.warn);
    setOpen(false);
    if (n.link) {
      navigate(n.link);
    }
  }

  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={s.bellBtn(isDark)}
        aria-label={`Notifications${unread ? `, ${unread} unread` : ''}`}
      >
        <span className="material-symbols-outlined">notifications</span>
        {unread > 0 ? <span style={s.badge}>{unread > 9 ? '9+' : unread}</span> : null}
      </button>

      {open ? (
        <>
          <div style={s.backdrop} onClick={() => setOpen(false)} aria-hidden />
          <div style={s.panel}>
            <div style={s.panelHead}>
              <strong>Notifications</strong>
              {unread > 0 ? (
                <button
                  type="button"
                  style={s.markAll}
                  onClick={() => markAllNotificationsRead(notifications)}
                >
                  Mark all read
                </button>
              ) : null}
            </div>
            {notifications.length === 0 ? (
              <p style={s.empty}>No notifications yet</p>
            ) : (
              <div style={s.list}>
                {notifications.map((n) => (
                  <button
                    key={n.id}
                    type="button"
                    style={s.item(!n.read)}
                    onClick={() => handleClick(n)}
                  >
                    <span style={s.itemTitle}>{n.title}</span>
                    {n.message ? <span style={s.itemMsg}>{n.message}</span> : null}
                    <span style={s.itemTime}>{formatTime(n.createdAt)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}

const s = {
  bellBtn: (dark) => ({
    position: 'relative',
    background: dark ? 'rgba(255,255,255,0.12)' : 'none',
    border: dark ? '1px solid rgba(255,255,255,0.2)' : '1.5px solid #e2e8f0',
    borderRadius: 10,
    padding: '8px 10px',
    cursor: 'pointer',
    color: dark ? '#fff' : '#475569',
    display: 'flex',
    alignItems: 'center',
  }),
  badge: {
    position: 'absolute',
    top: -4,
    right: -4,
    background: '#ef4444',
    color: '#fff',
    fontSize: 10,
    fontWeight: 800,
    borderRadius: 999,
    minWidth: 18,
    height: 18,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 4px',
  },
  backdrop: { position: 'fixed', inset: 0, zIndex: 998 },
  panel: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: 8,
    width: 320,
    maxHeight: 400,
    overflowY: 'auto',
    background: '#fff',
    borderRadius: 12,
    border: '1px solid #e2e8f0',
    boxShadow: '0 12px 36px rgba(0,0,0,0.12)',
    zIndex: 999,
  },
  panelHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 14px',
    borderBottom: '1px solid #f1f5f9',
    fontSize: 13,
  },
  markAll: {
    background: 'none',
    border: 'none',
    color: '#4b00d1',
    fontSize: 11,
    fontWeight: 700,
    cursor: 'pointer',
  },
  empty: { padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 13 },
  list: { display: 'flex', flexDirection: 'column' },
  item: (unread) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    padding: '12px 14px',
    border: 'none',
    borderBottom: '1px solid #f8fafc',
    background: unread ? '#f5f3ff' : '#fff',
    textAlign: 'left',
    cursor: 'pointer',
  }),
  itemTitle: { fontSize: 13, fontWeight: 700, color: '#1e293b' },
  itemMsg: { fontSize: 12, color: '#64748b', lineHeight: 1.4 },
  itemTime: { fontSize: 10, color: '#94a3b8', marginTop: 4 },
};
